import React, { useState } from 'react';
import {
  StarOutlined,
  SafetyOutlined,
  ClockCircleOutlined,
  ArrowUpOutlined,
  CheckCircleOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Card,
  Tabs,
  Typography,
  Row,
  Col,
  Statistic,
  Progress,
  Tag,
  Badge,
  Space,
  Timeline,
  Alert,
} from 'antd';
import type { TabsProps } from 'antd';
import TopSummarySection from '../../TopSummarySection';
import AllTransactions from '../../AllTransactions';
import { USERSESSIONOBJECT } from '../../../../Utils/storageUtils';
import {
  formatToINR,
  convertAmoutToINRWithNotation,
} from '../../../../Utils/utils';
import styles from './index.module.css';

const { Title, Text } = Typography;

type Props = {
  onBack: () => void;
};

// Realistic FD data - opened in 2023
const termDepositData = {
  dataRequestDetailsId: '0198d6c9-a86f-b52e-0709-08933f0c173f',
  rawData: {
    maskedAccNumber: 'XXXXXXXX9971',
    type: 'term_deposit',
    linkedAccRef: 'e24f1631-cc4b-4f7c-8406-ee8326c414cc',
    fipName: 'HDFC Bank',
    Profile: {
      Holders: {
        Holder: [
          {
            name: 'MR.KIRANKUMAR GODISELA',
            nominee: 'REGISTERED',
            address:
              'HNO 3-5-118/6/2,GROUND FLR,KRISHNA NAGAR,,GANDHI NGR CLNY,HYDERGUDA,,HYDERABAD,TELANGANA,500048',
            email: 'hello@gmail.com',
          },
        ],
        type: 'SINGLE',
      },
    },
    Summary: {
      branch: 'JayaNagar',
      ifsc: 'HDFC0001138',
      accountType: 'FIXED',
      maturityAmount: '543210',
      maturityDate: '2026-03-15',
      openingDate: '2023-03-15',
      description: '3 Year Fixed Deposit',
      interestPayout: 'OnMaturity',
      interestRate: '7.25',
      principalAmount: '450000',
      tenureYears: '3',
      interestComputation: 'COMPOUND',
      compoundingFrequency: 'QUARTERLY',
      interestOnMaturity: '93210',
      currentValue: '516342',
      currentBalance: '516342',
    },
  },
};

// Realistic RD data - started this year
const recurringDepositData = {
  dataRequestDetailsId: '0198d6c9-a870-22ca-13fb-904f38b9d20c',
  rawData: {
    maskedAccNumber: 'XXXXXXXX5432',
    type: 'recurring_deposit',
    linkedAccRef: '1930b937-0063-4288-8636-a765241cb1f7',
    fipName: 'ICICI Bank',
    Profile: {
      Holders: {
        Holder: [
          {
            name: 'MR.KIRANKUMAR GODISELA',
            DOB: '1986-10-07',
            nominee: 'REGISTERED',
            address:
              'HNO 3-5-118/6/2,GROUND FLR,KRISHNA NAGAR,,GANDHI NGR CLNY,HYDERGUDA,,HYDERABAD,TELANGANA,500048',
            email: 'hello@gmail.com',
          },
        ],
        type: 'SINGLE',
      },
    },
    Summary: {
      branch: 'Koramangala',
      openingDate: '2025-01-01',
      ifsc: 'ICIC0001256',
      accountType: 'RECURRING',
      maturityAmount: '131520',
      maturityDate: '2027-01-01',
      description: 'Monthly Savings RD',
      interestPayout: 'OnMaturity',
      interestRate: '6.75',
      principalAmount: '120000',
      tenureYears: '2',
      recurringAmount: '5000',
      interestComputation: 'COMPOUND',
      compoundingFrequency: 'QUARTERLY',
      interestOnMaturity: '11520',
      currentValue: '40000',
      currentBalance: '40000',
    },
  },
};

// Additional FD data
const additionalFD1 = {
  rawData: {
    maskedAccNumber: 'XXXXXXXX7890',
    type: 'term_deposit',
    fipName: 'SBI',
    Profile: {
      Holders: {
        Holder: [
          {
            name: 'MR.KIRANKUMAR GODISELA',
            nominee: 'REGISTERED',
            address:
              'HNO 3-5-118/6/2,GROUND FLR,KRISHNA NAGAR,,GANDHI NGR CLNY,HYDERGUDA,,HYDERABAD,TELANGANA,500048',
            email: 'hello@gmail.com',
          },
        ],
        type: 'SINGLE',
      },
    },
    Summary: {
      branch: 'MG Road',
      ifsc: 'SBIN0001234',
      accountType: 'FIXED',
      maturityAmount: '216540',
      maturityDate: '2025-12-10',
      openingDate: '2024-12-10',
      description: 'Tax Saver FD',
      interestPayout: 'OnMaturity',
      interestRate: '6.80',
      principalAmount: '200000',
      tenureYears: '1',
      interestComputation: 'COMPOUND',
      compoundingFrequency: 'QUARTERLY',
      interestOnMaturity: '16540',
      currentValue: '211230',
      currentBalance: '211230',
    },
  },
};

const additionalFD2 = {
  rawData: {
    maskedAccNumber: 'XXXXXXXX3456',
    type: 'term_deposit',
    fipName: 'Axis Bank',
    Profile: {
      Holders: {
        Holder: [
          {
            name: 'MR.KIRANKUMAR GODISELA',
            nominee: 'REGISTERED',
            address:
              'HNO 3-5-118/6/2,GROUND FLR,KRISHNA NAGAR,,GANDHI NGR CLNY,HYDERGUDA,,HYDERABAD,TELANGANA,500048',
            email: 'hello@gmail.com',
          },
        ],
        type: 'SINGLE',
      },
    },
    Summary: {
      branch: 'Whitefield',
      ifsc: 'UTIB0002345',
      accountType: 'FIXED',
      maturityAmount: '167831',
      maturityDate: '2026-06-20',
      openingDate: '2024-06-20',
      description: '2 Year FD',
      interestPayout: 'OnMaturity',
      interestRate: '7.10',
      principalAmount: '150000',
      tenureYears: '2',
      interestComputation: 'COMPOUND',
      compoundingFrequency: 'QUARTERLY',
      interestOnMaturity: '17831',
      currentValue: '160542',
      currentBalance: '160542',
    },
  },
};

// Mock transactions for FD and RD accounts
const generateMockTransactions = () => {
  const mockTransactions: any[] = [];

  // Use UUID-style codes that match the existing pattern, or specific bank codes
  const bankLogoCodes: { [key: string]: string } = {
    'HDFC Bank': '2e3f5a7b-43e4-11ed-9c98-80b65522ba8b', // HDFC logo code
    SBI: 'sbi-fip', // SBI logo code
    'Axis Bank': '4e5f7c9d-65g6-33gf-1eaa-a2d87744dc0d', // Axis logo code
    'ICICI Bank': '5f6g8dae-76h7-44hg-2fbb-b3e98855ed1e', // ICICI logo code
  };

  const accounts = [
    { accNo: 'XXXXXXXX9971', bankName: 'HDFC Bank', type: 'FD' },
    { accNo: 'XXXXXXXX7890', bankName: 'SBI', type: 'FD' },
    { accNo: 'XXXXXXXX3456', bankName: 'Axis Bank', type: 'FD' },
    { accNo: 'XXXXXXXX5432', bankName: 'ICICI Bank', type: 'RD' },
  ];

  const transactionTypes = [
    {
      name: 'Interest Credit',
      type: 'CREDIT',
      category: 'Interest',
      merchantCode: 'INTEREST',
    },
    {
      name: 'Monthly Deposit',
      type: 'CREDIT',
      category: 'Deposit',
      merchantCode: 'DEPOSIT',
    },
    {
      name: 'Maturity Proceeds',
      type: 'CREDIT',
      category: 'Maturity',
      merchantCode: 'MATURITY',
    },
    {
      name: 'Penalty Charges',
      type: 'DEBIT',
      category: 'Charges',
      merchantCode: 'PENALTY',
    },
    {
      name: 'Auto Renewal',
      type: 'CREDIT',
      category: 'Renewal',
      merchantCode: 'RENEWAL',
    },
    {
      name: 'Tax Deduction',
      type: 'DEBIT',
      category: 'Tax',
      merchantCode: 'TDS',
    },
  ];

  // Generate transactions for last 6 months
  const today = new Date();

  for (let monthOffset = 0; monthOffset < 6; monthOffset++) {
    const txnDate = new Date(today);
    txnDate.setMonth(txnDate.getMonth() - monthOffset);

    accounts.forEach((account) => {
      // For RD accounts, add monthly deposit transactions
      if (account.type === 'RD' && monthOffset < 8) {
        mockTransactions.push({
          id: `TXN${account.accNo}_${monthOffset}_DEP`,
          account: account.accNo,
          dateOfTransaction: txnDate.toISOString(),
          amount: 5000,
          type: 'CREDIT',
          displayName: `Deposit-${account.bankName} Monthly RD Deposit`,
          categoryCode: 'DEPOSIT',
          merchantLogoCode: '02e6af85-fea7-4dd7-bb32-0e650c133d07',
          description: 'Monthly Recurring Deposit',
          referenceNumber: `REF${Math.random()
            .toString(36)
            .substring(7)
            .toUpperCase()}`,
          balance: 40000 + monthOffset * 5000,
        });
      }

      // Add quarterly interest credits for FDs
      if (account.type === 'FD' && monthOffset % 3 === 0) {
        const interestAmount = Math.floor(Math.random() * 5000) + 2000;
        mockTransactions.push({
          id: `TXN${account.accNo}_${monthOffset}_INT`,
          account: account.accNo,
          dateOfTransaction: txnDate.toISOString(),
          amount: interestAmount,
          type: 'CREDIT',
          displayName: `Interest-${account.bankName} FD Interest Credit`,
          categoryCode: 'INTEREST',
          merchantLogoCode: '02e6af85-fea7-4dd7-bb32-0e650c133d07',
          description: 'Quarterly Interest Credit',
          referenceNumber: `REF${Math.random()
            .toString(36)
            .substring(7)
            .toUpperCase()}`,
          balance: 450000 + interestAmount,
        });
      }

      // Add TDS deduction for some transactions
      if (account.type === 'FD' && monthOffset === 3) {
        mockTransactions.push({
          id: `TXN${account.accNo}_${monthOffset}_TDS`,
          account: account.accNo,
          dateOfTransaction: txnDate.toISOString(),
          amount: Math.floor(Math.random() * 1000) + 500,
          type: 'DEBIT',
          displayName: `Tax-TDS on FD Interest`,
          categoryCode: 'TDS',
          merchantLogoCode: '02e6af85-fea7-4dd7-bb32-0e650c133d07',
          description: 'Tax Deducted at Source',
          referenceNumber: `REF${Math.random()
            .toString(36)
            .substring(7)
            .toUpperCase()}`,
          balance: 450000,
        });
      }
    });
  }

  return mockTransactions.sort(
    (a, b) =>
      new Date(b.dateOfTransaction).getTime() -
      new Date(a.dateOfTransaction).getTime()
  );
};

const Deposits: React.FC<Props> = ({ onBack }) => {
  // Initialize mock transactions in USERSESSIONOBJECT if not already present
  React.useEffect(() => {
    const mockTxns = generateMockTransactions();
    if (!USERSESSIONOBJECT.data) {
      USERSESSIONOBJECT.data = {
        allTransactions: mockTxns,
      };
    } else if (!USERSESSIONOBJECT.data.allTransactions) {
      USERSESSIONOBJECT.data.allTransactions = mockTxns;
    } else {
      // Filter out any existing FD/RD transactions to avoid duplicates
      const fdRdAccounts = [
        'XXXXXXXX9971',
        'XXXXXXXX7890',
        'XXXXXXXX3456',
        'XXXXXXXX5432',
      ];
      const existingTxns = USERSESSIONOBJECT.data.allTransactions.filter(
        (txn: any) => !fdRdAccounts.includes(txn.account)
      );
      USERSESSIONOBJECT.data.allTransactions = [...existingTxns, ...mockTxns];
    }
  }, []);
  const [deposits] = useState<any[]>([
    {
      id: 'FD001',
      accNo: termDepositData.rawData.maskedAccNumber,
      type: 'Fixed Deposit',
      balance: termDepositData.rawData.Summary.currentValue,
      maturityAmount: termDepositData.rawData.Summary.maturityAmount,
      interestRate: termDepositData.rawData.Summary.interestRate,
      maturityDate: termDepositData.rawData.Summary.maturityDate,
      openingDate: termDepositData.rawData.Summary.openingDate,
      principalAmount: termDepositData.rawData.Summary.principalAmount,
      ifscCode: termDepositData.rawData.Summary.ifsc,
      fipName: termDepositData.rawData.fipName,
      accType: termDepositData.rawData.Summary.accountType,
      holderName: termDepositData.rawData.Profile.Holders.Holder[0].name,
      holderAddress: termDepositData.rawData.Profile.Holders.Holder[0].address,
      holderEmail: termDepositData.rawData.Profile.Holders.Holder[0].email,
      icon: <SafetyOutlined />,
      color: '#1890ff',
      depositType: 'FD',
      tenure: termDepositData.rawData.Summary.tenureYears + ' Years',
      interestPayout: termDepositData.rawData.Summary.interestPayout,
      data: termDepositData,
    },
    {
      id: 'FD002',
      accNo: additionalFD1.rawData.maskedAccNumber,
      type: 'Fixed Deposit',
      balance: additionalFD1.rawData.Summary.currentValue,
      maturityAmount: additionalFD1.rawData.Summary.maturityAmount,
      interestRate: additionalFD1.rawData.Summary.interestRate,
      maturityDate: additionalFD1.rawData.Summary.maturityDate,
      openingDate: additionalFD1.rawData.Summary.openingDate,
      principalAmount: additionalFD1.rawData.Summary.principalAmount,
      ifscCode: additionalFD1.rawData.Summary.ifsc,
      fipName: additionalFD1.rawData.fipName,
      accType: additionalFD1.rawData.Summary.accountType,
      holderName: additionalFD1.rawData.Profile.Holders.Holder[0].name,
      holderAddress: additionalFD1.rawData.Profile.Holders.Holder[0].address,
      holderEmail: additionalFD1.rawData.Profile.Holders.Holder[0].email,
      icon: <SafetyOutlined />,
      color: '#ff4d4f',
      depositType: 'FD',
      tenure: additionalFD1.rawData.Summary.tenureYears + ' Year',
      interestPayout: additionalFD1.rawData.Summary.interestPayout,
      data: additionalFD1,
    },
    {
      id: 'FD003',
      accNo: additionalFD2.rawData.maskedAccNumber,
      type: 'Fixed Deposit',
      balance: additionalFD2.rawData.Summary.currentValue,
      maturityAmount: additionalFD2.rawData.Summary.maturityAmount,
      interestRate: additionalFD2.rawData.Summary.interestRate,
      maturityDate: additionalFD2.rawData.Summary.maturityDate,
      openingDate: additionalFD2.rawData.Summary.openingDate,
      principalAmount: additionalFD2.rawData.Summary.principalAmount,
      ifscCode: additionalFD2.rawData.Summary.ifsc,
      fipName: additionalFD2.rawData.fipName,
      accType: additionalFD2.rawData.Summary.accountType,
      holderName: additionalFD2.rawData.Profile.Holders.Holder[0].name,
      holderAddress: additionalFD2.rawData.Profile.Holders.Holder[0].address,
      holderEmail: additionalFD2.rawData.Profile.Holders.Holder[0].email,
      icon: <SafetyOutlined />,
      color: '#722ed1',
      depositType: 'FD',
      tenure: additionalFD2.rawData.Summary.tenureYears + ' Years',
      interestPayout: additionalFD2.rawData.Summary.interestPayout,
      data: additionalFD2,
    },
    {
      id: 'RD001',
      accNo: recurringDepositData.rawData.maskedAccNumber,
      type: 'Recurring Deposit',
      balance: recurringDepositData.rawData.Summary.currentValue,
      maturityAmount: recurringDepositData.rawData.Summary.maturityAmount,
      interestRate: recurringDepositData.rawData.Summary.interestRate,
      maturityDate: recurringDepositData.rawData.Summary.maturityDate,
      openingDate: recurringDepositData.rawData.Summary.openingDate,
      principalAmount: recurringDepositData.rawData.Summary.principalAmount,
      monthlyDeposit: recurringDepositData.rawData.Summary.recurringAmount,
      ifscCode: recurringDepositData.rawData.Summary.ifsc,
      fipName: recurringDepositData.rawData.fipName,
      accType: recurringDepositData.rawData.Summary.accountType,
      holderName: recurringDepositData.rawData.Profile.Holders.Holder[0].name,
      holderAddress:
        recurringDepositData.rawData.Profile.Holders.Holder[0].address,
      holderEmail: recurringDepositData.rawData.Profile.Holders.Holder[0].email,
      icon: <ClockCircleOutlined />,
      color: '#52c41a',
      depositType: 'RD',
      tenure: recurringDepositData.rawData.Summary.tenureYears + ' Years',
      interestPayout: recurringDepositData.rawData.Summary.interestPayout,
      data: recurringDepositData,
    },
  ]);

  const [activeKey, setActiveKey] = useState('1');

  // Calculate totals
  const totalValue = deposits.reduce(
    (sum, dep) => sum + parseFloat(dep.balance),
    0
  );
  const totalPrincipal = deposits.reduce(
    (sum, dep) => sum + parseFloat(dep.principalAmount),
    0
  );
  const totalMaturityAmount = deposits.reduce(
    (sum, dep) => sum + parseFloat(dep.maturityAmount),
    0
  );
  const totalFDValue = deposits
    .filter((d) => d.depositType === 'FD')
    .reduce((sum, dep) => sum + parseFloat(dep.balance), 0);
  const totalRDValue = deposits
    .filter((d) => d.depositType === 'RD')
    .reduce((sum, dep) => sum + parseFloat(dep.balance), 0);
  const averageInterestRate =
    deposits.reduce((sum, dep) => sum + parseFloat(dep.interestRate), 0) /
    deposits.length;

  // Calculate maturity progress
  const calculateProgress = (deposit: any) => {
    const start = new Date(deposit.openingDate).getTime();
    const end = new Date(deposit.maturityDate).getTime();
    const current = new Date().getTime(); // Use actual current date
    const progress = ((current - start) / (end - start)) * 100;
    return Math.min(Math.max(progress, 0), 100);
  };

  // Overview Component with all visualizations
  const DepositOverview = () => {
    const fdData = deposits.filter((d) => d.depositType === 'FD');
    const rdData = deposits.filter((d) => d.depositType === 'RD');

    // Interest rate comparison data
    const interestComparisonData = [
      { bank: 'HDFC Bank', rate: 7.25, type: 'Your FD' },
      { bank: 'SBI', rate: 6.8, type: 'Your FD' },
      { bank: 'Axis Bank', rate: 7.1, type: 'Your FD' },
      { bank: 'ICICI Bank', rate: 6.75, type: 'Your RD' },
      { bank: 'Good Bank', rate: 8.2, type: 'Suggested', special: true },
    ];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Total Summary Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={6}>
            <Card className={styles.summaryCard}>
              <Statistic
                title='Total Investment'
                value={totalPrincipal}
                formatter={(value) =>
                  `₹${convertAmoutToINRWithNotation(Number(value))}`
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card className={styles.summaryCard}>
              <Statistic
                title='Current Value'
                value={totalValue}
                valueStyle={{ color: '#52c41a' }}
                formatter={(value) =>
                  `₹${convertAmoutToINRWithNotation(Number(value))}`
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card className={styles.summaryCard}>
              <Statistic
                title='Expected Returns'
                value={totalMaturityAmount - totalPrincipal}
                valueStyle={{ color: '#1890ff' }}
                formatter={(value) =>
                  `₹${convertAmoutToINRWithNotation(Number(value))}`
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card className={styles.summaryCard}>
              <Statistic
                title='Avg Interest Rate'
                value={averageInterestRate.toFixed(2)}
                suffix='% p.a.'
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>

        {/* FD vs RD Distribution */}
        <Card title='FD vs RD Distribution' className={styles.infoCard}>
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12}>
              <div style={{ textAlign: 'center', padding: 20 }}>
                <div
                  style={{
                    position: 'relative',
                    width: 200,
                    height: 200,
                    margin: '0 auto',
                  }}
                >
                  <svg width='200' height='200'>
                    <circle
                      cx='100'
                      cy='100'
                      r='80'
                      fill='none'
                      stroke='#f0f0f0'
                      strokeWidth='40'
                    />
                    <circle
                      cx='100'
                      cy='100'
                      r='80'
                      fill='none'
                      stroke='#1890ff'
                      strokeWidth='40'
                      strokeDasharray={`${
                        (totalFDValue / totalValue) * 502
                      } 502`}
                      transform='rotate(-90 100 100)'
                    />
                    <text
                      x='100'
                      y='100'
                      textAnchor='middle'
                      dominantBaseline='middle'
                      fontSize='24'
                      fontWeight='bold'
                    >
                      {((totalFDValue / totalValue) * 100).toFixed(2)}%
                    </text>
                    <text
                      x='100'
                      y='120'
                      textAnchor='middle'
                      dominantBaseline='middle'
                      fontSize='14'
                      fill='#999'
                    >
                      Fixed Deposits
                    </text>
                  </svg>
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div style={{ padding: '20px 0' }}>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Fixed Deposits</Text>
                  <br />
                  <Text style={{ fontSize: 24, color: '#1890ff' }}>
                    ₹{convertAmoutToINRWithNotation(totalFDValue)}
                  </Text>
                  <Tag color='blue' style={{ marginLeft: 8 }}>
                    {fdData.length} FDs
                  </Tag>
                </div>
                <div>
                  <Text strong>Recurring Deposits</Text>
                  <br />
                  <Text style={{ fontSize: 24, color: '#52c41a' }}>
                    ₹{convertAmoutToINRWithNotation(totalRDValue)}
                  </Text>
                  <Tag color='green' style={{ marginLeft: 8 }}>
                    {rdData.length} RD
                  </Tag>
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Maturity Timeline */}
        <Card title='Maturity Timeline' className={styles.infoCard}>
          <div style={{ position: 'relative' }}>
            {/* You are here indicator */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 70,
                display: 'flex',
                alignItems: 'center',
                zIndex: 10,
              }}
            >
              {/*<div style={{ */}
              {/*  background: 'linear-gradient(90deg, #52c41a 0%, #73d13d 100%)',*/}
              {/*  color: 'white',*/}
              {/*  padding: '4px 12px',*/}
              {/*  borderRadius: '12px',*/}
              {/*  fontSize: 12,*/}
              {/*  fontWeight: 'bold',*/}
              {/*  boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)'*/}
              {/*}}>*/}
              {/*  /!*You are here*!/*/}
              {/*</div>*/}
              {/*<div style={{*/}
              {/*  width: 0,*/}
              {/*  height: 0,*/}
              {/*  borderLeft: '8px solid #52c41a',*/}
              {/*  borderTop: '4px solid transparent',*/}
              {/*  borderBottom: '4px solid transparent',*/}
              {/*  marginLeft: -1*/}
              {/*}} />*/}
            </div>

            <Timeline mode='left' style={{ marginTop: 20 }}>
              {deposits
                .sort(
                  (a, b) =>
                    new Date(a.maturityDate).getTime() -
                    new Date(b.maturityDate).getTime()
                )
                .map((dep, index) => {
                  const maturityTime = new Date(dep.maturityDate).getTime();
                  const currentTime = new Date().getTime();
                  const isPast = false;
                  const isNext = !isPast && index === 0;

                  return (
                    <Timeline.Item key={dep.id} color={'blue'} dot={undefined}>
                      <div>
                        <Text strong>
                          {dep.fipName} - {dep.depositType}
                        </Text>
                        <br />
                        <Text type='secondary'>{dep.accNo}</Text>
                        <br />
                        <Text>
                          Matures:{' '}
                          {new Date(dep.maturityDate).toLocaleDateString(
                            'en-IN',
                            {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            }
                          )}
                        </Text>
                        <br />
                        <Text strong style={{ color: '#52c41a' }}>
                          ₹
                          {convertAmoutToINRWithNotation(
                            parseFloat(dep.maturityAmount)
                          )}
                        </Text>
                      </div>
                    </Timeline.Item>
                  );
                })}
            </Timeline>
          </div>
        </Card>

        {/* Interest Rate Comparison - Vertical Bar Chart */}
        <Card
          title='Interest Rate Comparison'
          className={styles.infoCard}
          extra={
            <Button
              type='primary'
              icon={<TrophyOutlined />}
              onClick={() => window.open('https://www.ignosis.ai', '_blank')}
              style={{
                background: 'linear-gradient(90deg, #ff6b6b 0%, #ff8e53 100%)',
                border: 'none',
              }}
            >
              Apply for Good Bank FD @ 8.20%
            </Button>
          }
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-around',
              height: 250,
              padding: '20px 0',
            }}
          >
            {interestComparisonData.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 1,
                }}
              >
                <Text
                  strong
                  style={{
                    marginBottom: 8,
                    color: item.special ? '#ff6b6b' : '#1890ff',
                    fontSize: 16,
                  }}
                >
                  {item.rate}%
                </Text>
                <div
                  style={{
                    width: 60,
                    height: (item.rate / 10) * 200,
                    background: item.special
                      ? 'linear-gradient(180deg, #ff6b6b 0%, #ff8e53 100%)'
                      : item.type === 'Your RD'
                      ? '#52c41a'
                      : '#1890ff',
                    borderRadius: '8px 8px 0 0',
                    marginBottom: 8,
                    position: 'relative',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {item.special && (
                    <StarOutlined
                      style={{
                        position: 'absolute',
                        top: -20,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        color: 'gold',
                        fontSize: 18,
                      }}
                    />
                  )}
                </div>
                <Text style={{ fontSize: 11, textAlign: 'center' }}>
                  {item.bank}
                </Text>
                {item.type === 'Your FD' && (
                  <Tag color='blue' style={{ marginTop: 4, fontSize: 10 }}>
                    FD
                  </Tag>
                )}
                {item.type === 'Your RD' && (
                  <Tag color='green' style={{ marginTop: 4, fontSize: 10 }}>
                    RD
                  </Tag>
                )}
                {item.special && (
                  <Tag color='gold' style={{ marginTop: 4, fontSize: 10 }}>
                    Best
                  </Tag>
                )}
              </div>
            ))}
          </div>
          <Alert
            message='Good Bank Limited Time Offer!'
            description='Get up to 8.20% p.a. on your Fixed Deposits. Higher returns than your current deposits!'
            type='success'
            showIcon
            icon={<TrophyOutlined />}
            action={
              <Button
                size='small'
                type='primary'
                onClick={() => window.open('https://www.ignosis.ai', '_blank')}
              >
                Learn More
              </Button>
            }
          />
        </Card>
      </div>
    );
  };

  // FD Summary Component
  const FDSummary = () => {
    const fdDeposits = deposits.filter((d) => d.depositType === 'FD');

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {fdDeposits.map((fd) => (
          <Card key={fd.id} className={styles.infoCard}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={16}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 16,
                  }}
                >
                  {/*<Avatar*/}
                  {/*  style={{ backgroundColor: fd.color, marginRight: 12 }}*/}
                  {/*  icon={fd.icon}*/}
                  {/*  size={48}*/}
                  {/*/>*/}
                  <div>
                    <Text strong style={{ fontSize: 18 }}>
                      {fd.fipName}
                    </Text>
                    <br />
                    <Text type='secondary'>{fd.accNo}</Text>
                  </div>
                </div>
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={8}>
                    <Statistic
                      title='Principal Amount'
                      value={fd.principalAmount}
                      formatter={(value) =>
                        `₹${convertAmoutToINRWithNotation(Number(value))}`
                      }
                    />
                  </Col>
                  <Col xs={12} sm={8}>
                    <Statistic
                      title='Current Value'
                      value={fd.balance}
                      valueStyle={{ color: '#52c41a' }}
                      formatter={(value) =>
                        `₹${convertAmoutToINRWithNotation(Number(value))}`
                      }
                    />
                  </Col>
                  <Col xs={12} sm={8}>
                    <Statistic
                      title='Maturity Amount'
                      value={fd.maturityAmount}
                      valueStyle={{ color: '#1890ff' }}
                      formatter={(value) =>
                        `₹${convertAmoutToINRWithNotation(Number(value))}`
                      }
                    />
                  </Col>
                </Row>
              </Col>
              <Col xs={24} sm={8}>
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <Text type='secondary'>Interest Rate</Text>
                    <br />
                    <Text strong style={{ fontSize: 24, color: '#52c41a' }}>
                      {fd.interestRate}% p.a.
                    </Text>
                  </div>
                  <div>
                    <Text type='secondary'>Maturity Date</Text>
                    <br />
                    <Text strong>
                      {new Date(fd.maturityDate).toLocaleDateString('en-IN', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </Text>
                  </div>
                  <Progress
                    percent={parseFloat(calculateProgress(fd).toFixed(2))}
                    strokeColor='#52c41a'
                    size='small'
                    format={(percent) => `${percent?.toFixed(2)}%`}
                  />
                </div>
              </Col>
            </Row>
          </Card>
        ))}

        {/* Cross-sell Card */}
        <Card
          className={styles.infoCard}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          }}
        >
          <div style={{ textAlign: 'center', color: 'white' }}>
            <TrophyOutlined style={{ fontSize: 32, marginBottom: 16 }} />
            <Title level={4} style={{ color: 'white', marginBottom: 8 }}>
              Get Better Returns with Good Bank!
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
              Earn up to 8.20% p.a. on your Fixed Deposits
            </Text>
            <br />
            <Button
              type='primary'
              size='large'
              style={{
                marginTop: 16,
                background: '#fff',
                color: '#764ba2',
                border: 'none',
              }}
              onClick={() => window.open('https://www.ignosis.ai', '_blank')}
            >
              Apply Now <ArrowUpOutlined rotate={45} />
            </Button>
          </div>
        </Card>
      </div>
    );
  };

  // RD Summary Component
  const RDSummary = () => {
    const rdDeposits = deposits.filter((d) => d.depositType === 'RD');

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {rdDeposits.map((rd) => (
          <Card key={rd.id} className={styles.infoCard}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={16}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 16,
                  }}
                >
                  {/*<Avatar*/}
                  {/*  style={{ backgroundColor: rd.color, marginRight: 12 }}*/}
                  {/*  icon={rd.icon}*/}
                  {/*  size={48}*/}
                  {/*/>*/}
                  <div>
                    <Text strong style={{ fontSize: 18 }}>
                      {rd.fipName}
                    </Text>
                    <br />
                    <Text type='secondary'>{rd.accNo}</Text>
                  </div>
                </div>
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={8}>
                    <Statistic
                      title='Monthly Deposit'
                      value={rd.monthlyDeposit}
                      formatter={(value) =>
                        `₹${convertAmoutToINRWithNotation(Number(value))}`
                      }
                    />
                  </Col>
                  <Col xs={12} sm={8}>
                    <Statistic
                      title='Current Value'
                      value={rd.balance}
                      valueStyle={{ color: '#52c41a' }}
                      formatter={(value) =>
                        `₹${convertAmoutToINRWithNotation(Number(value))}`
                      }
                    />
                  </Col>
                  <Col xs={12} sm={8}>
                    <Statistic
                      title='Maturity Amount'
                      value={rd.maturityAmount}
                      valueStyle={{ color: '#1890ff' }}
                      formatter={(value) =>
                        `₹${convertAmoutToINRWithNotation(Number(value))}`
                      }
                    />
                  </Col>
                </Row>
              </Col>
              <Col xs={24} sm={8}>
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <Text type='secondary'>Interest Rate</Text>
                    <br />
                    <Text strong style={{ fontSize: 24, color: '#52c41a' }}>
                      {rd.interestRate}% p.a.
                    </Text>
                  </div>
                  <div>
                    <Text type='secondary'>Maturity Date</Text>
                    <br />
                    <Text strong>
                      {new Date(rd.maturityDate).toLocaleDateString('en-IN', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </Text>
                  </div>
                  <Progress
                    percent={parseFloat(calculateProgress(rd).toFixed(2))}
                    strokeColor='#52c41a'
                    size='small'
                    format={(percent) => `${percent?.toFixed(2)}%`}
                  />
                </div>
              </Col>
            </Row>
          </Card>
        ))}

        {/* Cross-sell Card for RD */}
        <Alert
          message='Start a New Recurring Deposit'
          description='Build your savings systematically with Good Bank RD at 7.50% p.a. Start with as low as ₹500/month!'
          type='info'
          showIcon
          icon={<TrophyOutlined />}
          action={
            <Button
              type='primary'
              onClick={() => window.open('https://www.ignosis.ai', '_blank')}
            >
              Start RD Now
            </Button>
          }
        />
      </div>
    );
  };

  const DepositSummary = ({ selectedDeposit }: { selectedDeposit: any }) => {
    const isRD = selectedDeposit.depositType === 'RD';
    const progress = calculateProgress(selectedDeposit);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Summary Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card className={styles.summaryCard}>
              <Statistic
                title={isRD ? 'Monthly Deposit' : 'Principal Amount'}
                value={
                  isRD
                    ? selectedDeposit.monthlyDeposit
                    : selectedDeposit.principalAmount
                }
                formatter={(value) =>
                  `₹${convertAmoutToINRWithNotation(Number(value))}`
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className={styles.summaryCard}>
              <Statistic
                title='Interest Rate'
                value={selectedDeposit.interestRate}
                suffix='% p.a.'
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card className={styles.summaryCard}>
              <Statistic
                title='Maturity Amount'
                value={selectedDeposit.maturityAmount}
                valueStyle={{ color: '#1890ff' }}
                formatter={(value) =>
                  `₹${convertAmoutToINRWithNotation(Number(value))}`
                }
              />
            </Card>
          </Col>
        </Row>

        {/* Deposit Details Card */}
        <Card title='Deposit Information' className={styles.infoCard}>
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={8}>
              <div className={styles.infoItem}>
                <Text type='secondary'>Account Type</Text>
                <br />
                <Text strong>{selectedDeposit.type}</Text>
              </div>
            </Col>
            <Col xs={12} sm={8}>
              <div className={styles.infoItem}>
                <Text type='secondary'>Tenure</Text>
                <br />
                <Text strong>{selectedDeposit.tenure}</Text>
              </div>
            </Col>
            <Col xs={12} sm={8}>
              <div className={styles.infoItem}>
                <Text type='secondary'>Maturity Date</Text>
                <br />
                <Text strong>
                  {new Date(selectedDeposit.maturityDate).toLocaleDateString(
                    'en-IN'
                  )}
                </Text>
              </div>
            </Col>
            <Col xs={12} sm={8}>
              <div className={styles.infoItem}>
                <Text type='secondary'>Opening Date</Text>
                <br />
                <Text strong>
                  {new Date(selectedDeposit.openingDate).toLocaleDateString(
                    'en-IN'
                  )}
                </Text>
              </div>
            </Col>
            <Col xs={12} sm={8}>
              <div className={styles.infoItem}>
                <Text type='secondary'>Interest Payout</Text>
                <br />
                <Text strong>{selectedDeposit.interestPayout}</Text>
              </div>
            </Col>
            <Col xs={12} sm={8}>
              <div className={styles.infoItem}>
                <Text type='secondary'>IFSC Code</Text>
                <br />
                <Text strong>{selectedDeposit.ifscCode}</Text>
              </div>
            </Col>
          </Row>

          {/* Progress Bar */}
          <div style={{ marginTop: 24 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <Text type='secondary'>Maturity Progress</Text>
              <Text type='secondary'>{progress.toFixed(2)}% Complete</Text>
            </div>
            <Progress
              percent={parseFloat(progress.toFixed(2))}
              strokeColor='#52c41a'
              showInfo={false}
            />
            {progress > 0 && progress < 100 && (
              <Text type='secondary' style={{ fontSize: 12, marginTop: 4 }}>
                You are here -{' '}
                {Math.ceil(
                  (new Date(selectedDeposit.maturityDate).getTime() -
                    new Date().getTime()) /
                    (1000 * 60 * 60 * 24)
                )}{' '}
                days to maturity
              </Text>
            )}
          </div>
        </Card>
      </div>
    );
  };

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: 'Overview',
      children: <DepositOverview />,
    },
    {
      key: '2',
      label: 'Fixed Deposits',
      children: <FDSummary />,
    },
    {
      key: '3',
      label: 'Recurring Deposits',
      children: <RDSummary />,
    },
    {
      key: '4',
      label: 'All Transactions',
      children: (
        <AllTransactions
          selectedAccounts={deposits.map((d) => d.accNo).join(',')}
        />
      ),
    },
  ];

  return (
    <div className={styles.depositsContainer}>
      <TopSummarySection
        isBackButton={true}
        onBack={onBack}
        subTitle={`${formatToINR(totalValue.toString())}`}
        title='Fixed & Recurring Deposits'
      />

      <div style={{ padding: '0 16px' }}>
        {/* Tabs */}
        <Tabs
          id='tabsDepositSummary'
          onChange={(e) => setActiveKey(e)}
          activeKey={activeKey}
          defaultActiveKey='1'
          items={items}
        />
      </div>
    </div>
  );
};

export default Deposits;
